import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { DeliveryNoteReferenceLotsSearch } from '@app/models/order/delivery-note';
import { ApiResponse, PaginatedApiResponse } from '@card-software/library';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { InventorySearchCommand } from '../../models/inventory-summary';
import { InventoryTransaction } from '../../models/inventory/inventory-transaction';
import { LotItem, LotItemInsert, MergeLotItemEntry, SplitLotItem } from '../../models/inventory/lot-item';
import { LotItemSearchCommand, LotItemSearchResultItem } from '../../models/inventory/lot-item-search';
import { ReturnSurplusSearchCommand } from '../../models/inventory/return-surplus-search';
import { ReturnSurplusRow } from '../../models/inventory/return-surplus-row';

@Injectable({
  providedIn: 'root',
})
export class LotItemProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.inventoryApi + '/api/v1/lot-item';

  // Pagination state signals
  public lotItemSearchPage = signal<number>(1);
  public lotItemSearchPageSize = signal<number>(50);

  public getInventoryLotByLotId(barcode: string): Observable<ApiResponse<LotItem>> {
    return this.httpClient.get<ApiResponse<LotItem>>(`${this.baseUrl}/lotId/${barcode}`);
  }

  public getByLotItemNumber(lotItemNumber: string): Observable<ApiResponse<LotItem>> {
    return this.httpClient.get<ApiResponse<LotItem>>(`${this.baseUrl}/number/${encodeURIComponent(lotItemNumber)}`);
  }

  public getByLotNumbers(lotNumbers: string[]): Observable<ApiResponse<LotItem[]>> {
    return this.httpClient.post<ApiResponse<LotItem[]>>(`${this.baseUrl}/batch/lot-numbers`, lotNumbers);
  }

  public getLotItemById(id: string): Observable<ApiResponse<LotItem>> {
    return this.httpClient.get<ApiResponse<LotItem>>(`${this.baseUrl}/${id}`);
  }

  public getLotsByInvoiceId(invoiceId: string, includeSplitLotItems = false): Observable<ApiResponse<LotItem[]>> {
    return this.httpClient.get<ApiResponse<LotItem[]>>(
      `${this.baseUrl}/invoice-reference/${invoiceId}?materializeLots=true&includeSplitLotItems=${includeSplitLotItems}`,
    );
  }

  public getLotItemByKnittingOrderId(id: string): Observable<ApiResponse<LotItem[]>> {
    return this.httpClient.post<ApiResponse<LotItem[]>>(`${this.baseUrl}/${id}/available-lot-items`, null);
  }

  public getInventoryLotByLotReceived(request: { wareHouseReference: string; lotIds: string[] }): Observable<ApiResponse<LotItem>> {
    return this.httpClient.post<ApiResponse<LotItem>>(`${this.baseUrl}/lots/received`, request);
  }

  public receiveLotItem(lotItemId: string, wareHouseReference: string): Observable<ApiResponse<LotItem>> {
    return this.httpClient.patch<ApiResponse<LotItem>>(
      `${this.baseUrl}/lot-item-id/${lotItemId}/receive?wareHouseReference=${wareHouseReference}`,
      {},
    );
  }

  public rollbackLotItem(lotItemId: string): Observable<ApiResponse<LotItem>> {
    return this.httpClient.post<ApiResponse<LotItem>>(`${this.baseUrl}/lot-id/${lotItemId}/rollback`, {});
  }

  public search(
    command: LotItemSearchCommand,
    pageNumber: number = this.lotItemSearchPage(),
    pageSize: number = this.lotItemSearchPageSize(),
    sortField?: string,
    sortOrder = -1,
  ): Observable<ApiResponse<PaginatedApiResponse<LotItemSearchResultItem>>> {
    let url = `${this.baseUrl}/search?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (sortField) {
      url += `&sortField=${encodeURIComponent(sortField)}&sortOrder=${sortOrder}`;
    }

    return this.httpClient.post<ApiResponse<PaginatedApiResponse<LotItemSearchResultItem>>>(url, command);
  }

  public getLotItemLabel(lotItemId: string): Observable<Blob> {
    return this.httpClient.get(
      `${environment.proxyUrls.inventoryApi}/api/v1/inventory-lot-image/download-table-with-qr-horizontal/${lotItemId}`,
      {
        responseType: 'blob',
      },
    );
  }

  public getLotsByDeliveryNoteIds(deliveryNoteReferenceLotsSearch: DeliveryNoteReferenceLotsSearch): Observable<ApiResponse<LotItem[]>> {
    return this.httpClient.post<ApiResponse<LotItem[]>>(
      `${this.baseUrl}/lot-item/delivery-note-reference/batch`,
      deliveryNoteReferenceLotsSearch,
    );
  }

  public splitLotItem(request: SplitLotItem[], lotId?: string): Observable<ApiResponse<LotItem[]>> {
    return this.httpClient.post<ApiResponse<LotItem[]>>(`${this.baseUrl}/${lotId}/split`, request);
  }

  public mergeLotItems(receiverLotId: string, request: MergeLotItemEntry[]): Observable<ApiResponse<LotItem>> {
    return this.httpClient.post<ApiResponse<LotItem>>(`${this.baseUrl}/${receiverLotId}/merge`, request);
  }

  public batchLotItem(request: string[]): Observable<ApiResponse<LotItem[]>> {
    return this.httpClient.post<ApiResponse<LotItem[]>>(`${this.baseUrl}/batch/ids`, request);
  }

  public putLotItem(lotItem: LotItem): Observable<ApiResponse<LotItem>> {
    return this.httpClient.put<ApiResponse<LotItem>>(`${this.baseUrl}/${lotItem.id}`, lotItem);
  }

  public CreateNewLotItem(
    knittingOrderId: string,
    newLotItemQuantity: number,
    lotItemNumberToCopy: string,
  ): Observable<ApiResponse<LotItem>> {
    return this.httpClient.post<ApiResponse<LotItem>>(
      `${this.baseUrl}/${knittingOrderId}/create-new?newLotItemQuantity=${newLotItemQuantity}&lotItemNumberToCopy=${encodeURIComponent(lotItemNumberToCopy)}`,
      null,
    );
  }

  public transferLotItemBetweenKnittingOrders(
    sourceKnittingOrderReference: string,
    targetKnittingOrderReference: string,
    targetFabricCode: string,
    targetVariantCode: string,
    lotItemNumber: string,
    quantityToTransfer: number,
  ): Observable<ApiResponse<LotItem>> {
    const params = {
      sourceKnittingOrderReference,
      targetKnittingOrderReference,
      targetFabricCode,
      targetVariantCode,
      lotItemNumber,
      quantityToTransfer: quantityToTransfer.toString(),
    };
    return this.httpClient.post<ApiResponse<LotItem>>(`${this.baseUrl}/transfer-between-knitting-orders`, null, { params });
  }

  public batchSearch(inventorySearchCommand: InventorySearchCommand): Observable<ApiResponse<LotItem[]>> {
    return this.httpClient.post<ApiResponse<LotItem[]>>(`${this.baseUrl}/search/batch`, inventorySearchCommand);
  }

  public createFromAdjustment(lotItemInsert: LotItemInsert): Observable<ApiResponse<LotItem>> {
    return this.httpClient.post<ApiResponse<LotItem>>(`${this.baseUrl}/from-adjustment`, lotItemInsert);
  }
  public receiveReturnSurplus(transactionId: string): Observable<ApiResponse<InventoryTransaction>> {
    return this.httpClient.post<ApiResponse<InventoryTransaction>>(`${this.baseUrl}/return-surplus/${transactionId}/receive`, null);
  }

  public searchReturnSurplus(
    command: ReturnSurplusSearchCommand,
    pageNumber = 1,
    pageSize = 25,
  ): Observable<ApiResponse<PaginatedApiResponse<ReturnSurplusRow>>> {
    return this.httpClient.post<ApiResponse<PaginatedApiResponse<ReturnSurplusRow>>>(
      `${this.baseUrl}/return-surplus/search?pageNumber=${pageNumber}&pageSize=${pageSize}`,
      command,
    );
  }

  public exportToExcelPaginated(command: LotItemSearchCommand): Observable<Blob> {
    {
      return this.httpClient.post(`${this.baseUrl}/search/export-excel`, command, {
        responseType: 'blob',
      });
    }
  }
}
